import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertProps } from 'antd';

const TOP_BANNER_STYLE: React.CSSProperties = {
	position: 'fixed',
	top: 16,
	left: '50%',
	transform: 'translateX(-50%)',
	zIndex: 1080,
	width: 'min(560px, calc(100vw - 32px))',
	boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
};

export type ApiErrorAlertProps = Omit<
	AlertProps,
	'message' | 'type' | 'onClose'
> & {
	/** When null, empty, or undefined, nothing is rendered. */
	message: string | null | undefined;
	type?: AlertProps['type'];
	/** Wired to Ant Design `onClose`; required for auto-hide and manual dismiss. */
	onDismiss?: () => void;
	/** `top`: fixed banner below viewport top (portal to `document.body`). `inline`: normal flow. */
	variant?: 'top' | 'inline';
	/**
	 * Milliseconds before calling `onDismiss`. `false` disables.
	 * Default: 3000 for `top`, disabled for `inline`.
	 */
	autoHideMs?: number | false;
};

/**
 * Shared alert for API / mutation errors. Pair with {@link getApiErrorMessage}.
 *
 * Default: fixed at top of viewport and auto-dismisses after 3 seconds.
 */
export const ApiErrorAlert: React.FC<ApiErrorAlertProps> = ({
	message,
	onDismiss,
	type = 'error',
	closable: closableProp,
	showIcon = true,
	variant = 'top',
	autoHideMs: autoHideMsProp,
	style,
	...rest
}) => {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const resolvedAutoHide =
		autoHideMsProp !== undefined
			? autoHideMsProp
			: variant === 'top'
				? 3000
				: false;

	const clearTimer = () => {
		if (timeoutRef.current != null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	};

	useEffect(() => {
		if (!message || resolvedAutoHide === false || !onDismiss) return;
		const ms = typeof resolvedAutoHide === 'number' ? resolvedAutoHide : 3000;
		clearTimer();
		timeoutRef.current = setTimeout(() => {
			timeoutRef.current = null;
			onDismiss();
		}, ms);
		return () => clearTimer();
	}, [message, resolvedAutoHide, onDismiss]);

	if (message == null || message === '') return null;

	const closable = closableProp ?? Boolean(onDismiss);

	const handleClose = () => {
		clearTimer();
		onDismiss?.();
	};

	const mergedStyle: React.CSSProperties =
		variant === 'top'
			? { ...TOP_BANNER_STYLE, ...style }
			: { ...(style ?? {}) };

	const alert = (
		<Alert
			{...rest}
			type={type}
			showIcon={showIcon}
			closable={closable}
			message={message}
			onClose={handleClose}
			style={mergedStyle}
		/>
	);

	if (variant === 'top' && typeof document !== 'undefined') {
		return createPortal(alert, document.body);
	}

	return alert;
};
